import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/toaster';

import Login from './pages/Login';
import Signup from './pages/Signup';
import AppLayout from './components/AppLayout';
import Dashboard from './pages/Dashboard';
import Directory from './pages/Directory';
import BusinessDetail from './pages/BusinessDetail';
import AuctionStore from './pages/AuctionStore';
import OnlineStore from './pages/OnlineStore';
import Loyalty from './pages/Loyalty';
import Community from './pages/Community';
import BlackoutGame from './pages/BlackoutGame';
import BlackoutInfo from './pages/BlackoutInfo';
import CommunityPage from './pages/CommunityPage';
import Messages from './pages/Messages';
import NewsletterView from './pages/NewsletterView';
import { NewsletterJoinPage, NewsletterConfirmPage, NewsletterUnsubscribePage } from './pages/NewsletterPublic';
import Coupons from './pages/Coupons';
import Dating from './pages/Dating';
import DatingRules from './pages/DatingRules';
import Events from './pages/Events';
import MyTickets from './pages/MyTickets';
import TicketDetail from './pages/TicketDetail';
import EventCheckoutSuccess from './pages/EventCheckoutSuccess';
import EventHostDashboard from './pages/EventHostDashboard';
import ResaleMarketplace from './pages/ResaleMarketplace';
import ResaleClaim from './pages/ResaleClaim';
import ResaleSuccess from './pages/ResaleSuccess';
import LandingPageBuilder from './pages/LandingPageBuilder';
import LandingPageView from './pages/LandingPageView';
import OwnerContacts from './pages/OwnerContacts';
import OnboardingSurveyModal from './components/OnboardingSurveyModal';
import SurveyBuilder from './pages/SurveyBuilder';
import SurveyResponses from './pages/SurveyResponses';
import SurveyPublic from './pages/SurveyPublic';
import ConfirmSliderSignup from './pages/ConfirmSliderSignup';
import CampaignsPage from './pages/CampaignsPage';
import CampaignDetail from './pages/CampaignDetail';
import AutoResponderPage from './pages/AutoResponderPage';
import AdminAutoResponder from './pages/AdminAutoResponder';
import Groups from './pages/Groups';
import GroupPublic from './pages/GroupPublic';
import OrganizerGroups from './pages/OrganizerGroups';
import MyInterests from './pages/MyInterests';
import RSSFeed from './pages/RSSFeed';
import Leaderboard from './pages/Leaderboard';
import Funds from './pages/Funds';
import Plans from './pages/Plans';
import HowItWorks from './pages/HowItWorks';
import Tracker from './components/Tracker';
import PaymentSuccess from './pages/PaymentSuccess';
import BusinessDashboard from './pages/BusinessDashboard';
import ManageListings from './pages/ManageListings';import ManageAuctions from './pages/ManageAuctions';
import MyCoupons from './pages/MyCoupons';
import MyLoyalty from './pages/MyLoyalty';
import BusinessReviews from './pages/BusinessReviews';
import UpgradePlan from './pages/UpgradePlan';
import Profile from './pages/Profile';
import Admin from './pages/Admin';
import AdminLoyalty from './pages/AdminLoyalty';
import Donate from './pages/Donate';
import DealBin from './pages/DealBin';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import LoyaltyCheckin from './pages/LoyaltyCheckin';
import AuctionCheckout from './pages/AuctionCheckout';
import AuctionCheckoutSuccess from './pages/AuctionCheckoutSuccess';
import ReviewCheckin from './pages/ReviewCheckin';
import ReviewInvite from './pages/ReviewInvite';
import EventCheckin from './pages/EventCheckin';
import PublicLayout from './components/PublicLayout';
import PublicHome from './pages/PublicHome';
import Psa from './pages/Psa';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to={`/login?redirect=${encodeURIComponent(window.location.pathname)}`} replace />;
  return children;
}

// Smart shell: authenticated users get the sidebar dashboard shell,
// logged-out visitors get the public marketing shell — both trees share
// the same read-only pages (directory, events, auction, store, etc.)
function SmartShell() {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <><AppLayout /><OnboardingSurveyModal /></> : <PublicLayout />;
}

function HomeSwitch() {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <Dashboard /> : <PublicHome />;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return null;
  if (user) {
    // Honor a one-shot post-auth redirect set by the signup / login page.
    // Fallback to home. This lets Business Owner signups land directly on
    // /business/manage instead of being bounced to the dashboard.
    const redir = new URLSearchParams(loc.search).get('redirect');
    let target = redir && redir.startsWith('/') && !redir.startsWith('//') ? redir : '/';
    try {
      const stored = sessionStorage.getItem('post_signup_redirect');
      if (stored) {
        sessionStorage.removeItem('post_signup_redirect');
        target = stored;
      }
    } catch (_e) { /* sessionStorage unavailable — fall through to '/' */ }
    return <Navigate to={target} replace />;
  }
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Tracker />
        <Routes>
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/signup" element={<PublicOnly><Signup /></PublicOnly>} />
          <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
          <Route path="/reset-password" element={<PublicOnly><ResetPassword /></PublicOnly>} />
          <Route path="/l/:slug" element={<LandingPageView />} />
          <Route path="/s/:slug" element={<SurveyPublic />} />
          <Route path="/confirm-signup/:token" element={<ConfirmSliderSignup />} />
          {/* Public "buy plan first, sign up after" flow — no shell, no auth. */}
          <Route path="/plans" element={<Plans />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          {/* PUBLIC-BROWSABLE tree (auth optional — engage-actions bounce to /login) */}
          <Route path="/" element={<SmartShell />}>
            <Route index element={<HomeSwitch />} />
            <Route path="marketing" element={<PublicHome />} />
            <Route path="psa" element={<Psa />} />
            <Route path="how-it-works" element={<HowItWorks />} />
            <Route path="directory" element={<Directory />} />
            <Route path="business/:id" element={<BusinessDetail />} />
            <Route path="auction" element={<AuctionStore />} />
            <Route path="auction/:aid/checkout" element={<Protected><AuctionCheckout /></Protected>} />
            <Route path="auction/:aid/checkout/success" element={<Protected><AuctionCheckoutSuccess /></Protected>} />
            <Route path="auction/:aid/checkout/cancel" element={<Protected><AuctionCheckout /></Protected>} />
            <Route path="dealbin" element={<DealBin />} />
            <Route path="donate" element={<Donate />} />
            <Route path="store" element={<OnlineStore />} />
            <Route path="loyalty" element={<Loyalty />} />
            <Route path="terms" element={<Terms />} />
            <Route path="privacy" element={<Privacy />} />
            <Route path="loyalty/checkin/:bid" element={<Protected><LoyaltyCheckin /></Protected>} />
            <Route path="reviews/checkin/:bid" element={<Protected><ReviewCheckin /></Protected>} />
            <Route path="review-invite/:token" element={<Protected><ReviewInvite /></Protected>} />
            <Route path="events/checkin/:eid" element={<Protected><EventCheckin /></Protected>} />
            <Route path="community" element={<Community />} />
            <Route path="messages" element={<Protected><Messages /></Protected>} />
            <Route path="newsletter" element={<Protected><NewsletterView /></Protected>} />
            <Route path="newsletter/join" element={<NewsletterJoinPage />} />
            <Route path="newsletter/confirm" element={<NewsletterConfirmPage />} />
            <Route path="newsletter/unsubscribe" element={<NewsletterUnsubscribePage />} />
            <Route path="community/members" element={<Protected><CommunityPage /></Protected>} />
            <Route path="blackout" element={<Protected><BlackoutGame /></Protected>} />
            <Route path="blackout/info" element={<BlackoutInfo />} />
            <Route path="coupons" element={<Coupons />} />
            <Route path="dating" element={<Protected><Dating /></Protected>} />
            <Route path="dating/rules" element={<DatingRules />} />
            <Route path="events" element={<Events />} />
            <Route path="events/:eid/success" element={<Protected><EventCheckoutSuccess /></Protected>} />
            <Route path="events/:eid/host" element={<Protected><EventHostDashboard /></Protected>} />
            <Route path="events/:eid/resale/:listingId/claim" element={<Protected><ResaleClaim /></Protected>} />
            <Route path="events/:eid/resale/:listingId/success" element={<Protected><ResaleSuccess /></Protected>} />
            <Route path="resale" element={<Protected><ResaleMarketplace /></Protected>} />
            <Route path="marketing/landing" element={<Protected><LandingPageBuilder /></Protected>} />
            <Route path="marketing/contacts" element={<Protected><OwnerContacts /></Protected>} />
            <Route path="marketing/surveys" element={<Protected><SurveyBuilder /></Protected>} />
            <Route path="marketing/surveys/:sid/responses" element={<Protected><SurveyResponses /></Protected>} />
            <Route path="marketing/campaigns" element={<Protected><CampaignsPage /></Protected>} />
            <Route path="marketing/campaigns/:cid" element={<Protected><CampaignDetail /></Protected>} />
            <Route path="marketing/auto-responder" element={<Protected><AutoResponderPage /></Protected>} />
            <Route path="admin/auto-responder" element={<Protected><AdminAutoResponder /></Protected>} />
            <Route path="groups" element={<Groups />} />
            <Route path="groups/mine" element={<Protected><OrganizerGroups /></Protected>} />
            <Route path="groups/:slug" element={<GroupPublic />} />
            <Route path="profile/interests" element={<Protected><MyInterests /></Protected>} />
            <Route path="my/tickets" element={<Protected><MyTickets /></Protected>} />
            <Route path="tickets/:ticketId" element={<Protected><TicketDetail /></Protected>} />
            <Route path="news" element={<RSSFeed />} />
            <Route path="leaderboard" element={<Leaderboard />} />
            <Route path="funds" element={<Funds />} />
            <Route path="business-dashboard" element={<Protected><BusinessDashboard /></Protected>} />
            <Route path="business/manage" element={<Protected><ManageListings /></Protected>} />
            <Route path="business/auctions" element={<Protected><ManageAuctions /></Protected>} />
            <Route path="business/coupons" element={<Protected><MyCoupons /></Protected>} />
            <Route path="business/loyalty" element={<Protected><MyLoyalty /></Protected>} />
            <Route path="business/reviews" element={<Protected><BusinessReviews /></Protected>} />
            <Route path="business/upgrade" element={<Protected><UpgradePlan /></Protected>} />
            <Route path="admin" element={<Protected><Admin /></Protected>} />
            <Route path="admin/loyalty" element={<Protected><AdminLoyalty /></Protected>} />
            <Route path="profile" element={<Protected><Profile /></Protected>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster />
    </AuthProvider>
  );
}

export default App;
